import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowLeft, CheckCircle, Clock, AlertCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const SuiviDossier = () => {
  const [searchParams] = useSearchParams();
  const initialRef = searchParams.get('ref') || '';
  
  const [reference, setReference] = useState(initialRef);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialRef) {
      handleSearch(initialRef);
    }
  }, [initialRef]);

  const handleSearch = async (refToSearch = reference) => {
    if (!refToSearch.trim()) {
      setError('Veuillez entrer un numéro de référence');
      return;
    }
    
    setLoading(true);
    setError('');
    setDossier(null);
    
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/demandes/track/${refToSearch}`);
      setDossier(res.data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setError("Aucun dossier trouvé pour cette référence. Veuillez vérifier le code.");
      } else {
        setError("Erreur lors de la recherche du dossier.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'Reçue':
        return { icon: <FileText className="w-8 h-8 text-slate-500" />, color: 'bg-slate-100 border-slate-300 text-slate-700', label: 'Dossier Reçu', text: 'Votre dossier a été enregistré et est en attente d\'attribution.' };
      case 'En évaluation':
        return { icon: <Clock className="w-8 h-8 text-blue-500" />, color: 'bg-blue-50 border-blue-200 text-blue-800', label: 'En Cours de Traitement', text: 'Un agent provincial analyse actuellement votre demande.' };
      case 'Clôturée':
        return { icon: <CheckCircle className="w-8 h-8 text-emerald-500" />, color: 'bg-emerald-50 border-emerald-200 text-emerald-800', label: 'Dossier Résolu / Clôturé', text: 'Le traitement de votre dossier est terminé.' };
      default:
        return { icon: <AlertCircle className="w-8 h-8 text-slate-500" />, color: 'bg-slate-100 border-slate-300 text-slate-700', label: status, text: '' };
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="bg-[#0A192F] text-white py-16 px-4 border-b-4 border-amber-500">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1 text-sm font-medium">
              <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
            </Link>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-500/20 text-amber-400 rounded-xl flex items-center justify-center border border-amber-500/30">
              <Search className="w-6 h-6" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold">Suivre mon dossier</h1>
          </div>
          <p className="text-blue-200 text-lg max-w-2xl">
            Consultez l'état d'avancement de votre demande sans avoir besoin de vous connecter. Munissez-vous du code de référence remis lors du dépôt.
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-6 md:p-10 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <input 
                type="text" 
                placeholder="Entrez votre N° de Référence (ex: A1B2C3D4)" 
                className="w-full pl-4 pr-4 py-4 rounded-xl border border-slate-300 text-slate-900 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-amber-500 uppercase"
                value={reference}
                onChange={(e) => setReference(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="input-suivi-reference"
              />
            </div>
            <Button 
              className="bg-[#0A192F] hover:bg-[#1E3A8A] text-white h-[60px] px-8 rounded-xl font-bold text-lg"
              onClick={() => handleSearch()}
              disabled={loading}
              data-testid="btn-suivi-rechercher"
            >
              {loading ? 'Recherche...' : 'Suivre'}
            </Button>
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}
        </div>

        {dossier && (
          <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
            <div className="p-6 md:p-10">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 border-b border-slate-100 pb-6">
                <div>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Dossier Réf.</p>
                  <h2 className="text-3xl font-black text-[#0A192F] font-mono tracking-tight">{dossier.reference}</h2>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-500 mb-1">Date de dépôt</p>
                  <p className="text-lg font-bold text-slate-800">
                    {new Date(dossier.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>

              <div className="mb-10">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Sujet de la demande</h3>
                <p className="text-slate-600 bg-slate-50 p-4 rounded-xl border border-slate-100 text-lg">
                  {dossier.sujet}
                </p>
              </div>

              {(() => {
                const statusInfo = getStatusInfo(dossier.status);
                return (
                  <div className={`p-6 rounded-2xl border-2 ${statusInfo.color} flex flex-col sm:flex-row items-center sm:items-start gap-6`}>
                    <div className="bg-white p-3 rounded-xl shadow-sm shrink-0">
                      {statusInfo.icon}
                    </div>
                    <div className="text-center sm:text-left">
                      <h3 className="text-xl font-bold mb-2">{statusInfo.label}</h3>
                      <p className="font-medium">{statusInfo.text}</p>
                      
                      {dossier.updated_at && (
                        <p className="mt-3 text-sm opacity-80 font-semibold">
                          Dernière mise à jour : {new Date(dossier.updated_at).toLocaleDateString('fr-FR')} à {new Date(dossier.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            
            <div className="bg-slate-50 p-6 border-t border-slate-200 text-center text-sm text-slate-500 font-medium">
              Pour des raisons de confidentialité, les détails personnels et les documents liés à ce dossier ne sont pas affichés publiquement. Connectez-vous à votre espace citoyen pour y accéder.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuiviDossier;
